'use client';

import type { Editor } from '@tiptap/react';
import { type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { useCallback, useMemo } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ListIcon } from '@/components/tiptap-editor/tiptap-icons/list-icon';
import { ListOrderedIcon } from '@/components/tiptap-editor/tiptap-icons/list-ordered-icon';
import { ListOrderedLetterIcon } from '@/components/tiptap-editor/tiptap-icons/list-ordered-letter-icon';
import { ListTodoIcon } from '@/components/tiptap-editor/tiptap-icons/list-todo-icon';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';

// --- Tiptap UI ---
import { canToggleList, isListActive, type ListType, type OrderedListType } from '@/components/tiptap-editor/tiptap-ui/list-button';

/**
 * Configuration for the list dropdown menu functionality
 */
export interface UseListDropdownMenuConfig {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;
    /**
     * The list types to display in the dropdown.
     * @default ["bulletList", "orderedList", "taskList"]
     */
    types?: ListType[];
    /**
     * Whether the dropdown should be hidden when no list types are available
     * @default false
     */
    hideWhenUnavailable?: boolean;
}

export interface ListOption {
    label: string;
    type: ListType;
    icon: React.ElementType;
    orderedListType?: OrderedListType;
}

export const listOptions: ListOption[] = [
    {
        label: 'Bullet List',
        type: 'bulletList',
        icon: ListIcon,
    },
    {
        label: 'Number List',
        type: 'orderedList',
        icon: ListOrderedIcon,
    },
    {
        label: 'Letter List',
        type: 'orderedList',
        orderedListType: 'a',
        icon: ListOrderedLetterIcon,
    },
    {
        label: 'Task List',
        type: 'taskList',
        icon: ListTodoIcon,
    },
];

export function canToggleAnyList(editor: Editor | null, listTypes: ListType[]): boolean {
    if (!editor || !editor.isEditable) return false;
    return listTypes.some(type => canToggleList(editor, type));
}

export function isAnyListActive(editor: Editor | null, listTypes: ListType[]): boolean {
    if (!editor || !editor.isEditable) return false;
    return listTypes.some(type => (type === 'orderedList' ? editor.isActive('orderedList') : isListActive(editor, type)));
}

export function getFilteredListOptions(availableTypes: ListType[]): typeof listOptions {
    return listOptions.filter(option => !option.type || availableTypes.includes(option.type));
}

export function shouldShowListDropdown(params: {
    editor: Editor | null;
    listTypes: ListType[];
    hideWhenUnavailable: boolean;
    listInSchema: boolean;
    canToggleAny: boolean;
}): boolean {
    const { editor, hideWhenUnavailable, listInSchema, canToggleAny } = params;

    if (!listInSchema || !editor) {
        return false;
    }

    if (hideWhenUnavailable && !editor.isActive('code')) {
        return canToggleAny;
    }

    return true;
}

/**
 * Gets the currently active list option from the available options (most specific match first, e.g. orderedList with type 'a' before plain orderedList).
 */
export function getActiveListOption(editor: Editor | null, options: ListOption[]): ListOption | undefined {
    if (!editor || !editor.isEditable) return undefined;
    const withOrderedListType = options.filter(o => o.orderedListType != null);
    const withoutOrderedListType = options.filter(o => o.orderedListType == null);
    for (const option of [...withOrderedListType, ...withoutOrderedListType]) {
        if (isListActive(editor, option.type, option.orderedListType)) return option;
    }
    return undefined;
}

/**
 * Custom hook that provides list dropdown menu functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MyListDropdown() {
 *   const {
 *     isVisible,
 *     activeType,
 *     isAnyActive,
 *     canToggleAny,
 *     filteredLists,
 *   } = useListDropdownMenu()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <DropdownMenu>
 *       // dropdown content
 *     </DropdownMenu>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedListDropdown() {
 *   const {
 *     isVisible,
 *     activeType,
 *   } = useListDropdownMenu({
 *     editor: myEditor,
 *     types: ["bulletList", "orderedList"],
 *     hideWhenUnavailable: true,
 *   })
 *
 *   // component implementation
 * }
 * ```
 */

const DEFAULT_LIST_TYPES: ListType[] = ['bulletList', 'orderedList', 'taskList'];

type ListDropdownSnapshot = {
    isVisible: boolean;
    canToggleAny: boolean;
    isAnyActive: boolean;
    activeList: ListOption | undefined;
};

function listDropdownSnapshotEquality(a: ListDropdownSnapshot, b: ListDropdownSnapshot | null): boolean {
    if (a.isVisible !== b?.isVisible || a.canToggleAny !== b?.canToggleAny || a.isAnyActive !== b?.isAnyActive) {
        return false;
    }
    const x = a.activeList;
    const y = b?.activeList;
    if (x === undefined && y === undefined) return true;
    if (!x || !y) return false;
    return x.type === y.type && x.orderedListType === y.orderedListType;
}

export function useListDropdownMenu(config?: UseListDropdownMenuConfig) {
    const { editor: providedEditor, types: typesProp, hideWhenUnavailable = false } = config || {};
    const types = useMemo(() => typesProp ?? DEFAULT_LIST_TYPES, [typesProp]);
    const filteredLists = useMemo(() => getFilteredListOptions(types), [types]);

    const { editor } = useTiptapEditor(providedEditor);

    const listDropdownSelector = useCallback(
        (ctx: EditorStateSnapshot<Editor | null>): ListDropdownSnapshot => {
            const ed = ctx.editor;
            if (!ed) {
                return { isVisible: false, canToggleAny: false, isAnyActive: false, activeList: undefined };
            }
            const listInSchema = types.some(type => isNodeInSchema(type, ed));
            const canToggleAny = canToggleAnyList(ed, types);
            const isVisible = shouldShowListDropdown({
                editor: ed,
                listTypes: types,
                hideWhenUnavailable,
                listInSchema,
                canToggleAny,
            });
            const isAnyActive = isAnyListActive(ed, types);
            const activeList = getActiveListOption(ed, filteredLists);
            return { isVisible, canToggleAny, isAnyActive, activeList };
        },
        [types, hideWhenUnavailable, filteredLists],
    );

    const listDropdown = useEditorState({
        editor,
        selector: listDropdownSelector,
        equalityFn: listDropdownSnapshotEquality,
    });

    const isVisible = listDropdown?.isVisible ?? false;
    const canToggleAny = listDropdown?.canToggleAny ?? false;
    const isAnyActive = listDropdown?.isAnyActive ?? false;
    const activeList = listDropdown?.activeList;
    const activeType = activeList?.type;

    return {
        isVisible,
        activeType,
        activeList,
        isActive: isAnyActive,
        canToggle: canToggleAny,
        types,
        filteredLists,
        label: 'List',
        Icon: activeList?.icon ?? ListIcon,
    };
}
